import React, { useState } from 'react';
import { X, Download, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import WordViewer from './WordViewer';
import ExcelViewer from './ExcelViewer';
import ImageViewer from './ImageViewer';
import PDFViewer from './PDFViewer';
import HTMLViewer from './HTMLViewer';

interface DocumentViewerProps {
  fileUrl: string;
  fileName: string;
  onClose: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ fileUrl, fileName, onClose }) => {
  const [zoom, setZoom] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Détecter le type de fichier
  const getFileType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    // PDF
    if (extension === 'pdf') return 'pdf';

    // Images
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
      return 'image';
    }

    // Word
    if (['doc', 'docx'].includes(extension)) return 'word';

    // Excel
    if (['xls', 'xlsx', 'csv'].includes(extension)) return 'excel';

    // HTML
    if (['html', 'htm'].includes(extension)) return 'html';

    // Texte
    if (['txt', 'log', 'md'].includes(extension)) return 'text';

    return 'unknown';
  };

  const fileType = getFileType(fileName);

  console.log('📁 DocumentViewer - fileName:', fileName);
  console.log('🔍 DocumentViewer - fileType:', fileType);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const renderViewer = () => {
    switch (fileType) {
      case 'pdf':
        return <PDFViewer fileUrl={fileUrl} zoom={zoom} />;
      case 'image':
        return <ImageViewer fileUrl={fileUrl} fileName={fileName} zoom={zoom} />;
      case 'word':
        return <WordViewer fileUrl={fileUrl} zoom={zoom} />;
      case 'excel':
        return <ExcelViewer fileUrl={fileUrl} zoom={zoom} />;
      case 'html':
        return <HTMLViewer fileUrl={fileUrl} zoom={zoom} />;
      case 'text':
        return (
          <div className="p-6 bg-white rounded-lg" style={{ transform: `scale(${zoom / 100})` }}>
            <iframe
              src={fileUrl}
              className="w-full h-full min-h-[600px] border-0"
              title={fileName}
            />
          </div>
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center h-96 bg-gray-50 rounded-lg">
            <p className="text-gray-600 mb-4">
              Prévisualisation non disponible pour ce type de fichier
            </p>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              <Download className="w-4 h-4" />
              Télécharger le fichier
            </button>
          </div>
        );
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ${
        isFullscreen ? 'p-0' : ''
      }`}
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-lg shadow-xl flex flex-col ${
          isFullscreen ? 'w-full h-full rounded-none' : 'w-full max-w-6xl max-h-[90vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-4 flex-1">
            <h3 className="font-semibold text-gray-900 truncate">{fileName}</h3>
            <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded uppercase">
              {fileType}
            </span>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {fileType !== 'unknown' && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="p-2 hover:bg-gray-200 rounded"
                  title="Zoom arrière"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium px-2">{zoom}%</span>
                <button
                  onClick={handleZoomIn}
                  className="p-2 hover:bg-gray-200 rounded"
                  title="Zoom avant"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <div className="w-px h-6 bg-gray-300 mx-2" />
              </>
            )}

            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-gray-200 rounded"
              title="Plein écran"
            >
              <Maximize2 className="w-4 h-4" />
            </button>

            <button
              onClick={handleDownload}
              className="p-2 hover:bg-gray-200 rounded"
              title="Télécharger"
            >
              <Download className="w-4 h-4" />
            </button>

            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded"
              title="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Viewer Content */}
        <div className="flex-1 overflow-auto p-4 bg-gray-100">
          {renderViewer()}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
